import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { preferencesAPI, llorosAPI } from '../services/api';
import { useAuth } from './AuthContext';

type HealthMode = 'headaches' | 'tensions' | 'lloros';

interface HealthModeContextType {
  healthMode: HealthMode;
  setHealthMode: (mode: HealthMode) => void;
  saveAsDefaultMode: (mode: HealthMode) => Promise<void>;
  isLoading: boolean;
  canUseLloros: boolean;
}

const HealthModeContext = createContext<HealthModeContextType | undefined>(undefined);

export const useHealthMode = () => {
  const context = useContext(HealthModeContext);
  if (context === undefined) {
    throw new Error('useHealthMode must be used within a HealthModeProvider');
  }
  return context;
};

interface HealthModeProviderProps {
  children: ReactNode;
}

export const HealthModeProvider: React.FC<HealthModeProviderProps> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [healthMode, setHealthMode] = useState<HealthMode>('headaches');
  const [isLoading, setIsLoading] = useState(true);
  const [canUseLloros, setCanUseLloros] = useState(false);

  // Cargar preferencia de modo por defecto al iniciar SOLO si el usuario está autenticado
  useEffect(() => {
    const loadDefaultMode = async () => {
      // No cargar preferencias si no está autenticado o aún está cargando auth
      if (!isAuthenticated || authLoading) {
        setIsLoading(false);
        return;
      }

      try {
        // Verificar si el usuario puede usar lloros
        const llorosAccess = await llorosAPI.canAccess();
        if (llorosAccess.data.success) {
          setCanUseLloros(llorosAccess.data.data.canAccess);
        }
      } catch (error) {
        console.log('Could not check lloros access');
        setCanUseLloros(false);
      }

      try {
        const response = await preferencesAPI.get('default_health_mode');
        if (response.data.success) {
          const savedMode = response.data.data.value as HealthMode;
          if (['headaches', 'tensions', 'lloros'].includes(savedMode)) {
            // Si el modo guardado es lloros pero el usuario no tiene acceso, usar headaches
            if (savedMode === 'lloros' && !canUseLloros) {
              setHealthMode('headaches');
            } else {
              setHealthMode(savedMode);
            }
          }
        }
      } catch (error) {
        console.log('No default health mode found, using headaches');
      } finally {
        setIsLoading(false);
      }
    };

    loadDefaultMode();
  }, [isAuthenticated, authLoading, canUseLloros]);

  const saveAsDefaultMode = async (mode: HealthMode) => {
    try {
      await preferencesAPI.set('default_health_mode', mode);
      console.log(`Saved ${mode} as default health mode`);
    } catch (error) {
      console.error('Error saving default health mode:', error);
      throw error;
    }
  };

  const handleModeChange = async (mode: HealthMode) => {
    setHealthMode(mode);
    // Automáticamente guardar como preferencia por defecto
    try {
      await saveAsDefaultMode(mode);
    } catch (error) {
      console.error('Error saving mode preference:', error);
    }
  };

  return (
    <HealthModeContext.Provider value={{
      healthMode,
      setHealthMode: handleModeChange,
      saveAsDefaultMode,
      isLoading,
      canUseLloros
    }}>
      {children}
    </HealthModeContext.Provider>
  );
};